import { writable, get } from "svelte/store";
import { ClusterService } from "$/services/ClusterService";
import { TopicService } from "$/services/TopicService";
import { BackgroundService } from "$/services/BackgroundService";
import { v4 as uuid } from "uuid";
import { changeElementPosition, getRole } from "$/utils/Utils";
import { LocalStorage } from "$/services/LocalStorage";
import { CacheService, EntityType } from "$/services/CacheService";
import { StoreStatus, StoreStatusState } from "$/models/StoreStatus";
import { TopicAccessLevel, } from "$/stores/extension/ActiveTopicStore";
const ClusterServiceImpl = (chrome === null || chrome === void 0 ? void 0 : chrome.storage) ? BackgroundService : ClusterService;
const TopicServiceImpl = (chrome === null || chrome === void 0 ? void 0 : chrome.storage) ? BackgroundService : TopicService;
const CacheServiceImpl = (chrome === null || chrome === void 0 ? void 0 : chrome.storage) ? BackgroundService : CacheService;
export var ClusterType;
(function (ClusterType) {
    ClusterType["TENANT_DEFAULT"] = "TENANT_DEFAULT";
    ClusterType["SHARED_WITH_ME"] = "SHARED_WITH_ME";
    ClusterType["SHARED"] = "SHARED";
    ClusterType["TENANT"] = "TENANT";
    ClusterType["ARCHIVED"] = "ARCHIVED";
    ClusterType["RESTRICTED"] = "RESTRICTED";
})(ClusterType || (ClusterType = {}));
export var ClusterAccessLevel;
(function (ClusterAccessLevel) {
    ClusterAccessLevel[ClusterAccessLevel["RESTRICTED"] = 0] = "RESTRICTED";
    ClusterAccessLevel[ClusterAccessLevel["WORKSPACE"] = 1] = "WORKSPACE";
    ClusterAccessLevel[ClusterAccessLevel["PUBLIC"] = 2] = "PUBLIC";
})(ClusterAccessLevel || (ClusterAccessLevel = {}));
export const ClusterTypeOrder = [
    ClusterType.TENANT_DEFAULT,
    ClusterType.TENANT,
    ClusterType.ARCHIVED,
    ClusterType.RESTRICTED,
    ClusterType.SHARED_WITH_ME,
    ClusterType.SHARED,
];
var SpecialClusterId;
(function (SpecialClusterId) {
    SpecialClusterId["TENANT_DEFAULT"] = "00000000-0000-0000-0000-000000000000";
    SpecialClusterId["ARCHIVED"] = "00000000-0000-0000-0000-000000000001";
    SpecialClusterId["SHARED_WITH_ME"] = "00000000-0000-0000-0000-000000000002";
})(SpecialClusterId || (SpecialClusterId = {}));
const calculateWeight = (elementBefore, elementAfter) => {
    let weight = 0;
    if (elementBefore && elementAfter)
        weight =
            (elementBefore.weight - elementAfter.weight) / 2 +
                elementAfter.weight;
    else if (elementBefore && !elementAfter)
        weight = elementBefore.weight / 2;
    else if (!elementBefore && elementAfter)
        weight = elementAfter.weight + 1000;
    return Math.round(weight);
};
const store = () => {
    // create store
    const { subscribe, set, update } = writable({
        active: null,
        clusters: null,
        state: new StoreStatusState()
    });
    return {
        subscribe,
        set,
        update,
        setStatus: (status, tenantId) => {
            let state = get(ClusterStore).state;
            state.setStatus(tenantId, status);
            update(store => (Object.assign(Object.assign({}, store), { state })));
        },
        init: async (tenantId) => {
            ClusterStore.setStatus(StoreStatus.INITIAL, tenantId);
            let clusters = await CacheServiceImpl.getAllEntities(EntityType.CLUSTER, tenantId);
            if (clusters.length > 0) {
                // remove special ids
                clusters = clusters.map(cluster => {
                    if (Object.values(SpecialClusterId).includes(cluster.id)) {
                        return Object.assign(Object.assign({}, cluster), { id: null });
                    }
                    return cluster;
                });
                // populate store from cache
                update(store => (Object.assign(Object.assign({}, store), { clusters })));
                ClusterStore.setStatus(StoreStatus.UPDATING, tenantId);
                // load asynchronously
                ClusterStore.load(tenantId);
            }
            else {
                // set status
                ClusterStore.setStatus(StoreStatus.LOADING, tenantId);
                // load synchronously
                await ClusterStore.load(tenantId);
            }
        },
        load: async (tenantId) => {
            let clusters = await ClusterServiceImpl.getClusters();
            clusters = await ClusterStore.structureClusterData(clusters);
            // update store state
            let state = get(ClusterStore).state;
            // update store
            if (state.shouldApplyUpdate(tenantId)) {
                update(store => (Object.assign(Object.assign({}, store), { clusters })));
                ClusterStore.setStatus(StoreStatus.UP_TO_DATE, tenantId);
                // just update cache
            }
            else {
                let cacheClusters = clusters.map(cluster => {
                    return Object.assign(Object.assign({}, cluster), { id: ClusterStore.getSpecializedId(cluster) });
                });
                CacheServiceImpl.setEntities(EntityType.CLUSTER, cacheClusters, tenantId);
            }
        },
        structureClusterData: async (clusters) => {
            var _a;
            const tenantId = (_a = clusters[0]) === null || _a === void 0 ? void 0 : _a.tenantId;
            const localStorageKey = `collapsed_state_${tenantId}`;
            const savedState = await LocalStorage.get(localStorageKey);
            clusters = clusters.map((cluster) => {
                var _a, _b;
                const savedCluster = savedState === null || savedState === void 0 ? void 0 : savedState.find((saved) => cluster.clusterType === ClusterType.ARCHIVED
                    ? false
                    : (cluster.id !== null && saved.id === cluster.id) ||
                        (cluster.id === null &&
                            saved.clusterType === cluster.clusterType));
                const tenantId = cluster.tenantId;
                const localStorageKey = `collapsed_state_${tenantId}`;
                let updatedCluster = Object.assign(Object.assign({}, cluster), { collapsed: savedCluster
                        ? savedCluster.collapsed
                        : cluster.clusterType === ClusterType.ARCHIVED, topics: cluster.topics.map((topic) => {
                        var _a, _b, _c;
                        let clusterId = cluster.id;
                        let clusterType = cluster.clusterType;
                        if (topic.archived) {
                            clusterId = (_a = topic.clusterId) !== null && _a !== void 0 ? _a : null;
                            clusterType =
                                (_c = (_b = clusters.find((cluster) => cluster.id === topic.clusterId)) === null || _b === void 0 ? void 0 : _b.clusterType) !== null && _c !== void 0 ? _c : ClusterType.TENANT_DEFAULT;
                        }
                        return Object.assign(Object.assign({}, topic), { clusterId: clusterId, cluster: {
                                id: clusterId,
                                clusterType: clusterType,
                            } });
                    }) });
                (_a = updatedCluster.topics) === null || _a === void 0 ? void 0 : _a.sort((a, b) => a.weight - b.weight);
                (_b = updatedCluster.clusterRoles) === null || _b === void 0 ? void 0 : _b.sort((a, b) => {
                    return b.role - a.role;
                });
                return updatedCluster;
            });
            const simplifiedClusters = clusters.map(({ id, clusterType, collapsed }) => ({
                id,
                clusterType,
                collapsed,
            }));
            await LocalStorage.set(localStorageKey, simplifiedClusters);
            clusters.sort((a, b) => {
                return (new Date(a.createdOn).getTime() -
                    new Date(b.createdOn).getTime());
            });
            clusters.sort((a, b) => {
                return b.accessLevel - a.accessLevel;
            });
            clusters.sort((a, b) => {
                return b.weight - a.weight;
            });
            clusters.sort((a, b) => {
                return (ClusterTypeOrder.indexOf(a.clusterType) -
                    ClusterTypeOrder.indexOf(b.clusterType));
            });
            return clusters;
        },
        clear: () => {
            set({
                active: null,
                clusters: null,
                state: new StoreStatusState()
            });
        },
        sortClusters: (clusters) => {
            clusters.sort((a, b) => {
                return (new Date(a.createdOn).getTime() -
                    new Date(b.createdOn).getTime());
            });
            clusters.sort((a, b) => {
                return b.accessLevel - a.accessLevel;
            });
            clusters.sort((a, b) => {
                return a.weight - b.weight;
            });
            clusters.sort((a, b) => {
                return (ClusterTypeOrder.indexOf(a.clusterType) -
                    ClusterTypeOrder.indexOf(b.clusterType));
            });
        },
        findClusterById: (clusterId) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return null;
            return store.clusters.find((cluster) => cluster.id === clusterId);
        },
        createCluster: async (cluster) => {
            let newCluster = {
                id: cluster.id || uuid(),
                name: cluster.name || "New cluster",
                topics: [],
                clusterType: ClusterType.TENANT,
            };
            let store = get(ClusterStore);
            store.clusters.push(Object.assign(Object.assign({}, newCluster), { loading: true }));
            set(store);
            let createdCluster = await ClusterServiceImpl.createCluster(newCluster);
            let index = store.clusters.findIndex((c) => c.id === newCluster.id);
            store.clusters[index] = createdCluster;
            // Updating clusters with the same weight, this is happening in the back so we need to update it here also
            if (store.clusters.findIndex((c) => c.weight === createdCluster.weight &&
                c.id !== createdCluster.id &&
                c.clusterType === ClusterType.TENANT) > -1) {
                store.clusters = store.clusters.map((cluster) => cluster.id !== createdCluster.id &&
                    cluster.weight >= createdCluster.weight &&
                    cluster.clusterType === ClusterType.TENANT
                    ? Object.assign(Object.assign({}, cluster), { weight: cluster.weight + 1000 }) : cluster);
            }
            set(store);
            return newCluster;
        },
        updateCluster: async (cluster) => {
            let store = get(ClusterStore);
            let updatedCluster = await ClusterServiceImpl.updateCluster(cluster);
            let clusterIndex = store.clusters.findIndex((c) => c.id === updatedCluster.id);
            // cluster's topics array, collapsed and clusterType is not returned by api so we need to preserve it after update
            updatedCluster = Object.assign(Object.assign({}, updatedCluster), { collapsed: store.clusters[clusterIndex].collapsed, clusterType: store.clusters[clusterIndex].clusterType, topics: store.clusters[clusterIndex].topics });
            store.clusters.splice(clusterIndex, 1, updatedCluster);
            set(store);
        },
        deleteCluster: async (cluster) => {
            let store = get(ClusterStore);
            let index = store.clusters.findIndex((c) => c.id === cluster.id);
            store.clusters.splice(index, 1);
            set(store);
            ClusterStore.deleteClusterInBackground(cluster);
        },
        deleteClusterInBackground: async (cluster) => {
            try {
                // remove from server
                await ClusterServiceImpl.deleteCluster(cluster);
                // remove from cache
                await CacheServiceImpl.removeEntity(EntityType.CLUSTER, cluster.id, cluster.tenantId);
                //remove cluster from archived topics cluster (set topic's cluster to My Topics cluster)
                let store = get(ClusterStore);
                let archivedCluster = store.clusters.find((cluster) => cluster.clusterType === ClusterType.ARCHIVED);
                if (archivedCluster !== undefined) {
                    archivedCluster.topics = archivedCluster.topics.reduce((topic, nextTopic) => {
                        if (nextTopic.clusterId === cluster.id) {
                            nextTopic = Object.assign(Object.assign({}, nextTopic), { clusterId: null, cluster: {
                                    id: null,
                                    clusterType: ClusterType.TENANT_DEFAULT,
                                } });
                        }
                        return [...topic, nextTopic];
                    }, []);
                }
            }
            catch (error) {
                console.error('Failed to delete cluster in background:', error);
            }
        },
        changeClustersOrder: async (oldIndex, newIndex) => {
            let sortableClusters = ClusterStore.getSortableClustersSorted();
            // SortableJS lacks an option to reorder the list without animations (the dragged element pops out and follows the cursor).
            // To address this, we implemented a workaround that keeps the element in the list and shows only a placeholder bar indicating
            // where the element will be dropped. However, SortableJS still counts the element being moved during reordering,
            // which causes an issue when moving an item from top to bottom, as the element is considered first in the reordering process.
            if (oldIndex < newIndex)
                newIndex -= 1;
            changeElementPosition(sortableClusters, oldIndex, newIndex);
            const draggedCluster = sortableClusters[newIndex];
            const beforeCluster = newIndex === 0
                ? null
                : sortableClusters[newIndex - 1];
            const afterCluster = newIndex === sortableClusters.length - 1
                ? null
                : sortableClusters[newIndex + 1];
            let weight = calculateWeight(beforeCluster, afterCluster);
            let updatedCluster = {
                id: draggedCluster.id,
                weight: weight,
            };
            await ClusterStore.updateCluster(updatedCluster);
            // Updating clusters with the same weight, this is happening in the back so we need to update it here also
            if (sortableClusters.findIndex((cluster) => cluster.weight === weight) > -1) {
                let store = get(ClusterStore);
                store.clusters = store.clusters.map((cluster) => cluster.clusterType === ClusterType.TENANT &&
                    cluster.id !== draggedCluster.id &&
                    cluster.weight >= weight
                    ? Object.assign(Object.assign({}, cluster), { weight: cluster.weight + 1000 }) : cluster);
            }
        },
        changeTopicPositionInCluster: async (clusterId, clusterType, oldIndex, newIndex) => {
            if (oldIndex === newIndex || oldIndex + 1 === newIndex)
                return;
            let topicCluster = ClusterStore.getClusterByIdAndType(clusterId, clusterType);
            if (topicCluster === undefined)
                return;
            let sortedClusterTopics = topicCluster.topics.sort((a, b) => a.weight - b.weight);
            // SortableJS lacks an option to reorder the list without animations (the dragged element pops out and follows the cursor).
            // To address this, we implemented a workaround that keeps the element in the list and shows only a placeholder bar indicating
            // where the element will be dropped. However, SortableJS still counts the element being moved during reordering,
            // which causes an issue when moving an item from top to bottom, as the element is considered first in the reordering process.
            if (oldIndex < newIndex)
                newIndex -= 1;
            changeElementPosition(sortedClusterTopics, oldIndex, newIndex);
            const topic = sortedClusterTopics[newIndex];
            const beforeTopic = newIndex === 0 ? null : sortedClusterTopics[newIndex - 1];
            const afterTopic = newIndex === sortedClusterTopics.length - 1
                ? null
                : sortedClusterTopics[newIndex + 1];
            let weight = calculateWeight(afterTopic, beforeTopic);
            ClusterStore.updateClusterTopicsWithSameWeight(topic, weight);
            let updatedTopic = {
                id: topic.id,
                weight: weight,
            };
            ClusterStore.updateTopic(updatedTopic);
            await TopicServiceImpl.updateTopic(updatedTopic);
        },
        getTopicsWeightInClusterAtPositionIndex: (cluster, positionIndex) => {
            let topicCluster = ClusterStore.getClusterByIdAndType(cluster.id, cluster.clusterType);
            if (topicCluster === undefined)
                return;
            let sortedClusterTopics = topicCluster.topics.sort((a, b) => a.weight - b.weight);
            const beforeTopic = positionIndex === 0
                ? null
                : sortedClusterTopics[positionIndex - 1];
            const afterTopic = positionIndex === sortedClusterTopics.length - 1
                ? null
                : sortedClusterTopics[positionIndex];
            return calculateWeight(afterTopic, beforeTopic);
        },
        updateClusterTopicsWithSameWeight: (topic, weight) => {
            var _a;
            let store = get(ClusterStore);
            let clusterTopics = (_a = ClusterStore.getTopicCluster(topic)) === null || _a === void 0 ? void 0 : _a.topics;
            if (clusterTopics &&
                clusterTopics.findIndex((clusterTopic) => clusterTopic.id !== topic.id &&
                    clusterTopic.weight === weight) > -1) {
                let updatedClusterTopics = clusterTopics.map((clusterTopic) => clusterTopic.id !== topic.id &&
                    clusterTopic.weight >= weight
                    ? Object.assign(Object.assign({}, clusterTopic), { weight: clusterTopic.weight + 1000 }) : clusterTopic);
                store.clusters = store.clusters.map((cluster) => (topic.clusterId && cluster.id === topic.clusterId) ||
                    (!topic.clusterId &&
                        topic.cluster.clusterType === cluster.clusterType)
                    ? Object.assign(Object.assign({}, cluster), { topics: updatedClusterTopics }) : cluster);
                set(store);
            }
        },
        toggleCollapsed: async (clusterParam) => {
            let store = get(ClusterStore);
            const tenantId = clusterParam.tenantId;
            const localStorageKey = `collapsed_state_${tenantId}`;
            const clusters = store.clusters.map((cluster) => {
                if ((cluster.id !== null && cluster.id === clusterParam.id) ||
                    (cluster.id === null &&
                        cluster.clusterType === clusterParam.clusterType)) {
                    return Object.assign(Object.assign({}, cluster), { collapsed: !cluster.collapsed });
                }
                return cluster;
            });
            store = Object.assign(Object.assign({}, store), { clusters });
            set(store);
            const simplifiedClusters = clusters.map(({ id, clusterType, collapsed }) => ({
                id,
                clusterType,
                collapsed,
            }));
            await LocalStorage.set(localStorageKey, simplifiedClusters);
        },
        isCollapsed: (cluster, savedState = null) => {
            const source = savedState !== null && savedState !== void 0 ? savedState : get(ClusterStore);
            if (!source || !source.clusters || !cluster)
                return false;
            const matchingCluster = cluster.id !== null
                ? source.clusters.find((c) => c.id === cluster.id)
                : source.clusters.find((c) => c.clusterType === cluster.clusterType);
            if (savedState && cluster.clusterType === ClusterType.ARCHIVED) {
                return true;
            }
            return matchingCluster ? matchingCluster.collapsed : false;
        },
        leaveCluster: async (cluster) => {
            let store = get(ClusterStore);
            let index = store.clusters.findIndex((c) => c.id === cluster.id);
            store.clusters.splice(index, 1);
            set(store);
            // save to server
            await ClusterServiceImpl.leaveCluster(cluster.id);
        },
        createTopic: async (topic, cluster) => {
            topic = Object.assign(Object.assign({}, topic), { name: topic.name || "New topic", cluster: cluster.id ? { id: cluster.id } : null });
            topic = await TopicServiceImpl.createTopic(topic);
            // adding the missing cluster information after the server answer
            topic = Object.assign(Object.assign({}, topic), { clusterId: cluster.id, cluster: {
                    id: cluster.id,
                    clusterType: cluster.clusterType,
                } });
            let store = get(ClusterStore);
            let index = store.clusters.findIndex((c) => c.id === cluster.id);
            // add topic to cluster topics
            if (store.clusters[index].topics) {
                store.clusters[index].topics.push(topic);
            }
            else {
                store.clusters[index].topics = [topic];
            }
            set(store);
            // save to server
            return topic;
        },
        updateTopic: (topicFieldsToUpdate) => {
            let store = get(ClusterStore);
            let topic = ClusterStore.findTopic(topicFieldsToUpdate.id);
            // if topic is changing cluster, remove topic from old cluster and add it to the new one
            if (topicFieldsToUpdate.cluster) {
                ClusterStore.removeTopicFromAllClusters(topic.id);
                store.clusters.forEach((cluster) => {
                    if (cluster.id === topicFieldsToUpdate.cluster.id &&
                        topicFieldsToUpdate.cluster.clusterType ===
                            cluster.clusterType) {
                        cluster.topics.push(Object.assign(Object.assign({}, topic), topicFieldsToUpdate));
                        cluster.topics.sort((a, b) => b.weight - a.weight);
                    }
                });
            }
            else {
                store.clusters.forEach((cluster) => {
                    let index = cluster.topics.findIndex((t) => t.id === topic.id);
                    if (index > -1) {
                        cluster.topics[index] = Object.assign(Object.assign({}, topic), topicFieldsToUpdate);
                    }
                });
            }
            set(store);
        },
        deleteTopic: async (topicId) => {
            // delete from server
            await TopicServiceImpl.deleteTopic(topicId);
            // delete from cache
            let state = get(ClusterStore).state;
            await CacheServiceImpl.removeEntity(EntityType.TOPIC, topicId, state.id);
        },
        archiveTopic: async (topicId, archive) => {
            let store = get(ClusterStore);
            let topic = ClusterStore.findTopic(topicId);
            ClusterStore.removeTopicFromAllClusters(topicId);
            topic = Object.assign(Object.assign({}, topic), { archived: archive });
            // add/remove topic from cluster and sort topics
            store.clusters.reduce((cluterList, cluster) => {
                if (archive && cluster.clusterType === ClusterType.ARCHIVED) {
                    cluster.topics.push(topic);
                }
                else if (!archive &&
                    cluster.id === topic.cluster.id &&
                    cluster.clusterType === topic.cluster.clusterType) {
                    cluster.topics.push(topic);
                }
                return [...cluterList, cluster];
            }, []);
            set(store);
            let topicFieldsToUpdate = {
                id: topic.id,
                archived: archive,
            };
            await TopicServiceImpl.updateTopic(topicFieldsToUpdate);
        },
        unarchiveTopicToBottomOfList: async (topic) => {
            let store = get(ClusterStore);
            ClusterStore.removeTopicFromAllClusters(topic.id);
            topic = Object.assign(Object.assign({}, topic), { archived: false, weight: 0 });
            let topicCluster = ClusterStore.findClusterById(topic.clusterId);
            //ActiveTopic doesn't contain cluster so we set it for future use
            if (!topic.cluster) {
                if (topicCluster !== undefined) {
                    topicCluster = {
                        id: topicCluster.id,
                        clusterType: topicCluster.clusterType,
                    };
                }
                else {
                    topicCluster = {
                        id: null,
                        clusterType: ClusterType.TENANT_DEFAULT,
                    };
                }
                topic = Object.assign(Object.assign({}, topic), { cluster: topicCluster });
            }
            let cluster = ClusterStore.getClusterByIdAndType(topicCluster.id, topicCluster.clusterType);
            let maxWeight = (cluster === null || cluster === void 0 ? void 0 : cluster.topics.reduce((max, t) => {
                return Math.max(max, t.weight || 0);
            }, 0)) || 0;
            let weight = maxWeight + 1000;
            topic = Object.assign(Object.assign({}, topic), { weight: weight });
            store.clusters.reduce((cluterList, cluster) => {
                if (!topic.clusterId &&
                    cluster.clusterType === ClusterType.TENANT_DEFAULT) {
                    cluster.topics.push(topic);
                }
                else if (cluster.id === topic.clusterId &&
                    cluster.clusterType === topic.cluster.clusterType) {
                    cluster.topics.push(topic);
                }
                return [...cluterList, cluster];
            }, []);
            set(store);
            let topicFieldsToUpdate = {
                id: topic.id,
                archived: false,
                weight: weight,
            };
            await TopicServiceImpl.updateTopic(topicFieldsToUpdate);
            ClusterStore.updateClusterTopicsWithSameWeight(topic, weight);
        },
        findTopic: (topicId) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return null;
            let topic = null;
            store.clusters.forEach((cluster) => {
                let index = cluster.topics.findIndex((t) => t.id === topicId);
                if (index > -1) {
                    topic = cluster.topics[index];
                    return;
                }
            });
            return topic;
        },
        removeTopicFromAllClusters: (topicId) => {
            let store = get(ClusterStore);
            // remove topic from all clusters
            store.clusters.forEach((cluster) => {
                let index = cluster.topics.findIndex((t) => t.id === topicId);
                if (index > -1) {
                    cluster.topics.splice(index, 1);
                }
            });
            set(store);
        },
        getTopicRoles: (topicId, userId, returnAll) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return null;
            let roles = [];
            store.clusters.forEach((cluster) => {
                // find cluster with topic
                let index = cluster.topics.findIndex((t) => t.id === topicId);
                if (index > -1 && cluster.clusterRoles) {
                    let isOwner = cluster.clusterRoles.find((r) => r.role === TopicAccessLevel.OWNER &&
                        r.user.id === userId);
                    if (isOwner || returnAll) {
                        roles = cluster.clusterRoles;
                    }
                    else {
                        roles = cluster.clusterRoles.filter((r) => r.user.id === userId);
                    }
                    return;
                }
            });
            return roles;
        },
        getFirstTopic: () => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return null;
            let topic = null;
            store.clusters.forEach((cluster) => {
                if (topic === null &&
                    cluster.topics &&
                    cluster.topics.length > 0) {
                    topic = cluster.topics[0];
                    return;
                }
            });
            return topic;
        },
        getMyFirstTopic: (userId) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return null;
            let topic = null;
            let allTopics = [];
            // get all topics from all clusters
            store.clusters.forEach((cluster) => {
                if (cluster.topics && cluster.topics.length > 0) {
                    allTopics = allTopics.concat(cluster.topics);
                }
            });
            // find oldest topic where createdById is userId
            allTopics.forEach((t) => {
                if (t.createdById === userId) {
                    if (!topic ||
                        new Date(t.createdOn).getTime() <
                            new Date(topic.createdOn).getTime()) {
                        topic = t;
                    }
                }
            });
            return topic;
        },
        addTopicToCluster: (topic, clusterType) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return;
            let index = store.clusters.findIndex((c) => c.clusterType === clusterType);
            if (store.clusters[index].topics) {
                store.clusters[index].topics.push(topic);
            }
            else {
                store.clusters[index].topics = [topic];
            }
            set(store);
        },
        leaveTopic: async (topicId, topicRoleId) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return;
            store.clusters.forEach((cluster) => {
                let index = cluster.topics.findIndex((t) => t.id === topicId);
                if (index > -1) {
                    cluster.topics.splice(index, 1);
                }
            });
            set(store);
            await TopicServiceImpl.deleteTopicRole(topicId, topicRoleId);
        },
        copyTopic: async (tenantId, tenantStoreId, clusterId, topicId, copyComments) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return;
            const newTopic = await TopicServiceImpl.copyTopic(tenantId, topicId, clusterId, copyComments);
            // if copy was made to a different tenant, do not continue updating clusters
            if (tenantStoreId !== tenantId)
                return;
            // add new topic to cluster
            let updatedCluster = store.clusters.find((c) => c.id === clusterId);
            updatedCluster.topics = [...updatedCluster.topics, newTopic];
            // replace old cluster with updated one, including new topic
            let clusters;
            if (clusterId === null) {
                clusters = store.clusters.map((c) => c.clusterType === ClusterType.TENANT_DEFAULT
                    ? updatedCluster
                    : c);
            }
            else {
                clusters = store.clusters.map((c) => c.id === clusterId ? updatedCluster : c);
            }
            set(Object.assign(Object.assign({}, store), { active: null, clusters }));
        },
        changeTopicCluster: (topic, cluster) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return;
            // remove topic from old cluster
            if (topic.archived &&
                cluster.clusterType !== ClusterType.ARCHIVED) {
                let oldClusterIndex = store.clusters.findIndex((storeCluster) => storeCluster.clusterType === ClusterType.ARCHIVED);
                let topicIndex = store.clusters[oldClusterIndex].topics.findIndex((t) => t.id === topic.id);
                store.clusters[oldClusterIndex].topics.splice(topicIndex, 1);
            }
            else {
                let oldClusterIndex = store.clusters.findIndex((storeCluster) => storeCluster.id === topic.cluster.id &&
                    storeCluster.clusterType === topic.cluster.clusterType);
                let topicIndex = store.clusters[oldClusterIndex].topics.findIndex((t) => t.id === topic.id);
                store.clusters[oldClusterIndex].topics.splice(topicIndex, 1);
            }
            // add topic to new cluster
            let newClusterIndex = store.clusters.findIndex((storeCluster) => storeCluster.id === cluster.id &&
                storeCluster.clusterType === cluster.clusterType);
            let newCluster = store.clusters[newClusterIndex];
            topic = Object.assign(Object.assign({}, topic), { cluster: {
                    id: cluster.id,
                    clusterType: cluster.clusterType,
                } });
            // if it's not moving to archive we must set clusterId, we don't set it for archive because
            // we need to know from which cluster it originally came
            if (cluster.clusterType !== ClusterType.ARCHIVED) {
                topic = Object.assign(Object.assign({}, topic), { clusterId: cluster.id });
            }
            if (newCluster.topics) {
                newCluster.topics.push(topic);
            }
            else {
                newCluster.topics = [topic];
            }
            newCluster.topics.sort((a, b) => a.weight - b.weight);
            set(store);
        },
        createClusterRole: async (clusterRole, clusterId) => {
            if (!clusterRole.role || !clusterRole.user.username)
                return;
            clusterRole = Object.assign(Object.assign({}, clusterRole), { id: clusterRole.id || uuid() });
            let store = get(ClusterStore);
            let index = store.clusters.findIndex((c) => c.id === clusterId);
            let oldRoleIndex = store.clusters[index].clusterRoles.findIndex((role) => role.user.username === clusterRole.user.username);
            let user = {
                firstName: "Adding new user...",
                lastName: "",
                username: clusterRole.user.username,
            };
            if (oldRoleIndex !== -1) {
                user = store.clusters[index].clusterRoles[oldRoleIndex].user;
            }
            // add role to cluster roles
            if (store.clusters[index].clusterRoles) {
                if (oldRoleIndex !== -1) {
                    store.clusters[index].clusterRoles.splice(oldRoleIndex, 1);
                    store.clusters[index].clusterRoles.push(Object.assign(Object.assign({}, clusterRole), { user: user }));
                }
                else {
                    store.clusters[index].clusterRoles.push(Object.assign(Object.assign({}, clusterRole), { user: user }));
                }
            }
            else {
                store.clusters[index].clusterRoles = [
                    Object.assign(Object.assign({}, clusterRole), { user: user }),
                ];
            }
            // update active cluster
            store.active = store.clusters[index];
            set(store);
            let createdClusterRole = await ClusterServiceImpl.createClusterRole(clusterRole, clusterId);
            // update role in store
            let roleIndex = store.clusters[index].clusterRoles.findIndex((r) => r.id === clusterRole.id);
            store.clusters[index].clusterRoles[roleIndex] = createdClusterRole;
            store.active = store.clusters[index];
            set(store);
            return createdClusterRole;
        },
        updateClusterRole: async (clusterRole, clusterId) => {
            let store = get(ClusterStore);
            // update role in all clusters
            store.clusters.forEach((cluster) => {
                var _a;
                let index = (_a = cluster.clusterRoles) === null || _a === void 0 ? void 0 : _a.findIndex((r) => r.id === clusterRole.id);
                if (index > -1) {
                    cluster.clusterRoles[index].role = clusterRole.role;
                }
            });
            set(store);
            await ClusterServiceImpl.updateClusterRole(clusterRole, clusterId);
        },
        deleteClusterRole: async (clusterRoleId, clusterId) => {
            let store = get(ClusterStore);
            // remove role from all clusters
            store.clusters.forEach((cluster) => {
                var _a;
                let index = (_a = cluster.clusterRoles) === null || _a === void 0 ? void 0 : _a.findIndex((r) => r.id === clusterRoleId);
                if (index > -1) {
                    cluster.clusterRoles.splice(index, 1);
                }
            });
            set(store);
            await ClusterServiceImpl.deleteClusterRole(clusterRoleId, clusterId);
        },
        updateClusterRolesUser: (user) => {
            let store = get(ClusterStore);
            if (!store.clusters)
                return;
            store.clusters.forEach((cluster) => {
                if (cluster.clusterRoles) {
                    cluster.clusterRoles = cluster.clusterRoles.map((role) => role.user.id === user.id
                        ? Object.assign(Object.assign({}, role), { user: Object.assign(Object.assign({}, role.user), { firstName: user.firstName, lastName: user.lastName }) }) : role);
                }
            });
            set(store);
        },
        getAllClusterTopics: () => {
            var _a;
            return (_a = get(ClusterStore)
                .clusters) === null || _a === void 0 ? void 0 : _a.map((cluster) => cluster.topics).flat();
        },
        getAllNonArchivedTopics: () => {
            var _a;
            return (_a = get(ClusterStore)
                .clusters) === null || _a === void 0 ? void 0 : _a.map((cluster) => cluster.topics).flat().filter((topic) => !topic.archived);
        },
        getSharedClusters: () => {
            var _a;
            return (_a = get(ClusterStore).clusters) === null || _a === void 0 ? void 0 : _a.filter((cluster) => [
                ClusterType.RESTRICTED,
                ClusterType.SHARED_WITH_ME,
                ClusterType.SHARED,
            ].includes(cluster.clusterType));
        },
        getSortableClustersSorted: () => {
            var _a;
            return (_a = get(ClusterStore)
                .clusters) === null || _a === void 0 ? void 0 : _a.filter((cluster) => cluster.clusterType === ClusterType.TENANT ||
                cluster.clusterType === ClusterType.RESTRICTED).sort((a, b) => {
                return b.weight - a.weight;
            });
        },
        getFirstNonArchivedTopic: () => {
            var _a;
            const allTopics = ClusterStore.getAllClusterTopics();
            const firstNonArchivedTopic = allTopics.find((topic) => !topic.archived);
            if (firstNonArchivedTopic !== undefined)
                return firstNonArchivedTopic;
            return (_a = allTopics[0]) !== null && _a !== void 0 ? _a : null;
        },
        getTopic(topicId) {
            return ClusterStore.getAllClusterTopics().find((topic) => topic.id === topicId);
        },
        getClusterByIdAndType: (id, type) => {
            var _a, _b;
            return (_b = (_a = get(ClusterStore)) === null || _a === void 0 ? void 0 : _a.clusters) === null || _b === void 0 ? void 0 : _b.find((cluster) => cluster.id === id && cluster.clusterType === type);
        },
        getClustersByType: (type) => {
            var _a, _b, _c;
            return ((_c = (_b = (_a = get(ClusterStore)) === null || _a === void 0 ? void 0 : _a.clusters) === null || _b === void 0 ? void 0 : _b.filter((cluster) => cluster.clusterType === type)) !== null && _c !== void 0 ? _c : []);
        },
        getTopicCluster: (topic) => {
            var _a;
            return (_a = get(ClusterStore)) === null || _a === void 0 ? void 0 : _a.clusters.find((cluster) => (topic.clusterId && cluster.id === topic.clusterId) ||
                (!topic.clusterId &&
                    topic.cluster.clusterType === cluster.clusterType));
        },
        removeClustersByType: (typesToRemove) => {
            let store = get(ClusterStore);
            store.clusters = store.clusters.filter((cluster) => !typesToRemove.includes(cluster.clusterType));
            set(store);
        },
        removeClustersWithTopicsWithoutEditRole: (userId) => {
            let store = get(ClusterStore);
            store.clusters = store.clusters.reduce((accumulator, cluster) => {
                var _a;
                if (cluster.clusterType === ClusterType.TENANT_DEFAULT ||
                    cluster.clusterType === ClusterType.ARCHIVED ||
                    cluster.clusterType === ClusterType.TENANT)
                    accumulator.push(cluster);
                if (cluster.clusterType === ClusterType.SHARED &&
                    ((_a = cluster.clusterRoles) === null || _a === void 0 ? void 0 : _a.some((role) => role.role >= TopicAccessLevel.EDITOR &&
                        role.userId === userId)))
                    accumulator.push(cluster);
                if (cluster.clusterType === ClusterType.SHARED_WITH_ME) {
                    cluster.topics = cluster.topics.filter((topic) => {
                        var _a;
                        return (_a = topic.roles) === null || _a === void 0 ? void 0 : _a.some((role) => role.userId === userId &&
                            role.role >= TopicAccessLevel.EDITOR);
                    });
                    if (cluster.topics.length)
                        accumulator.push(cluster);
                }
                return accumulator;
            }, []);
            set(store);
        },
        isOwner: (userId) => {
            var _a;
            let clusterRoles = (_a = get(ClusterStore).active) === null || _a === void 0 ? void 0 : _a.clusterRoles;
            return (clusterRoles &&
                clusterRoles.some((clusterRole) => {
                    var _a;
                    return ((_a = clusterRole.user) === null || _a === void 0 ? void 0 : _a.id) === userId &&
                        clusterRole.role === TopicAccessLevel.OWNER;
                }));
        },
        getSpecializedId: (cluster) => {
            if (cluster.clusterType === ClusterType.TENANT_DEFAULT) {
                return SpecialClusterId.TENANT_DEFAULT;
            }
            else if (cluster.clusterType === ClusterType.ARCHIVED) {
                return SpecialClusterId.ARCHIVED;
            }
            else if (cluster.clusterType === ClusterType.SHARED_WITH_ME) {
                return SpecialClusterId.SHARED_WITH_ME;
            }
            return cluster.id;
        }
    };
};
export const ClusterStore = store();
ClusterStore.subscribe(async (store) => {
    if (store.clusters && store.state.id) {
        // NOTE: If we directly work with store.clusters svelte actually updates them, that's why we're
        //       in need for additional variable and mapping of store.clusters otherwise store will be
        //       left with specialized IDs after cache update
        let clusters = store.clusters.map(cluster => {
            return Object.assign(Object.assign({}, cluster), { id: ClusterStore.getSpecializedId(cluster) });
        });
        CacheServiceImpl.setEntities(EntityType.CLUSTER, clusters, store.state.id);
    }
});
